import {
  JsonFormat,
  numberJsonFormat,
  objectJsonFormat,
} from "@redotech/json/format";
import { HidContext, HidRequestContext } from "@redotech/react-util/hid";
import { useHandler } from "@redotech/react-util/hook";
import { useLoader } from "@redotech/react-util/load";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  splitWeight,
  Weight,
  WeightUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import CheckIcon from "@redotech/redo-icons/check.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import { RedoCommandMenu } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { hidInputReport } from "@redotech/scale/hid";
import { SCALE_FILTERS, scaleReadWeight } from "@redotech/scale/scale";
import { WeightUnit as ScaleWeightUnit } from "@redotech/scale/weight";
import { jsonStore, localStore } from "@redotech/web-util/storage";
import {
  memo,
  MouseEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useSafeInteractWithScale } from "./interact-with-scale-context";
import { ImperialWeightInput, MetricWeightInput } from "./weight-input";

export type WeightChangeSource = "scale";

export const TotalWeightWidget = memo(function TotalWeightWidget({
  weight,
  onWeightChange,
  onWeightReset,
  disabled = false,
  title = "Total weight",
  titleIcon,
  weightFormat,
  hideScale,
  disableAutoReadFromScale,
}: {
  weight: Weight | null;
  onWeightChange: (weight: Weight, source?: WeightChangeSource) => void;
  onWeightReset?: () => void;
  disabled?: boolean;
  title?: string;
  titleIcon?: React.ReactNode;
  weightFormat: WeightFormat;
  hideScale?: boolean;
  disableAutoReadFromScale?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const interactWithScale = useSafeInteractWithScale();

  const [lb, setLb] = useState<number | undefined>();
  const [oz, setOz] = useState<number | undefined>();

  const [kg, setKg] = useState<number | undefined>();
  const [g, setG] = useState<number | undefined>();

  useLayoutEffect(() => {
    if (ref.current?.contains(document.activeElement)) {
      return;
    }

    if (weight) {
      const [lb, oz] = splitWeight(weight, [
        WeightUnit.POUND,
        WeightUnit.OUNCE,
      ]);
      setLb(lb);
      setOz(oz);

      const [kg, g] = splitWeight(weight, [
        WeightUnit.KILOGRAM,
        WeightUnit.GRAM,
      ]);
      setKg(kg);
      setG(g);
    } else {
      setLb(undefined);
      setOz(undefined);
      setKg(undefined);
      setG(undefined);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight?.unit, weight?.value]);

  const updateLb = useHandler((lb: number) => {
    const newOz = oz !== undefined ? oz : 0; // if mixed, set oz to 0
    setLb(lb);
    setOz(newOz);
    onWeightChange({ value: lb * 16 + newOz, unit: WeightUnit.OUNCE });
  });

  const updateOz = useHandler((oz: number) => {
    const newLb = lb !== undefined ? lb : 0; // if mixed, set lb to 0
    setLb(newLb);
    setOz(oz);
    onWeightChange({ value: newLb * 16 + oz, unit: WeightUnit.OUNCE });
  });

  const updateKg = useHandler((kg: number) => {
    const newG = g !== undefined ? g : 0; // if mixed, set g to 0
    setKg(kg);
    setG(newG);
    onWeightChange({ value: kg * 1000 + newG, unit: WeightUnit.GRAM });
  });

  const updateG = useHandler((g: number) => {
    const newKg = kg !== undefined ? kg : 0; // if mixed, set kg to 0
    setKg(newKg);
    setG(g);
    onWeightChange({ value: newKg * 1000 + g, unit: WeightUnit.GRAM });
  });

  const handleLbBlur = useHandler((text: string) => {
    if (!weight) {
      return;
    }
    if (text.includes(".")) {
      // clear oz and normalize
      const weight = { value: +text || 0, unit: WeightUnit.POUND };
      const [lb, oz] = splitWeight(weight, [
        WeightUnit.POUND,
        WeightUnit.OUNCE,
      ]);
      setLb(lb);
      setOz(oz);
      onWeightChange(weight);
    } else {
      // normalize
      const [lb, oz] = splitWeight(weight, [
        WeightUnit.POUND,
        WeightUnit.OUNCE,
      ]);
      setLb(lb);
      setOz(oz);
    }
  });

  const handleOzBlur = useHandler(() => {
    if (!weight) {
      return;
    }
    // normalize
    const [lb, oz] = splitWeight(weight, [WeightUnit.POUND, WeightUnit.OUNCE]);
    setLb(lb);
    setOz(oz);
  });

  const handleKgBlur = useHandler((text: string) => {
    if (!weight) {
      return;
    }
    if (text.includes(".")) {
      // normalize
      const weight = { value: +text || 0, unit: WeightUnit.KILOGRAM };
      const [kg, g] = splitWeight(weight, [
        WeightUnit.KILOGRAM,
        WeightUnit.GRAM,
      ]);
      setKg(kg);
      setG(g);
      onWeightChange(weight);
    } else {
      // normalize
      const [kg, g] = splitWeight(weight, [
        WeightUnit.KILOGRAM,
        WeightUnit.GRAM,
      ]);
      setKg(kg);
      setG(g);
    }
  });

  const handleGBlur = useHandler(() => {
    if (!weight) {
      return;
    }
    // normalize
    const [kg, g] = splitWeight(weight, [WeightUnit.KILOGRAM, WeightUnit.GRAM]);
    setKg(kg);
    setG(g);
  });

  return (
    <Flex dir="column" gap="xs" w="full">
      <Flex align="center" justify="space-between">
        <Flex gap="xs">
          <Text fontSize="xs">{title}</Text>
          {titleIcon}
        </Flex>
      </Flex>
      <Flex align="center" dir="row" gap="md">
        {weightFormat === WeightFormat.METRIC ? (
          <MetricWeightInput
            disabled={disabled}
            g={g}
            handleGBlur={handleGBlur}
            handleKgBlur={handleKgBlur}
            interactWithScale={interactWithScale}
            kg={kg}
            ref={ref}
            updateG={updateG}
            updateKg={updateKg}
            weight={weight}
          />
        ) : (
          <ImperialWeightInput
            disabled={disabled}
            handleLbBlur={handleLbBlur}
            handleOzBlur={handleOzBlur}
            interactWithScale={interactWithScale}
            lb={lb}
            oz={oz}
            ref={ref}
            updateLb={updateLb}
            updateOz={updateOz}
            weight={weight}
          />
        )}
        {hideScale ? null : (
          <ScaleButton
            disableAutoReadFromScale={disableAutoReadFromScale}
            disabled={disabled}
            onWeightChange={onWeightChange}
          />
        )}
      </Flex>
      {onWeightReset ? (
        <Flex dir="row" justify="flex-end" mt="sm">
          <RedoButton
            disabled={disabled}
            onClick={onWeightReset}
            size="xs"
            text="Reset"
          />
        </Flex>
      ) : null}
    </Flex>
  );
});

interface DeviceId {
  productId: number;
  vendorId: number;
}

const deviceIdFormat: JsonFormat<DeviceId> = objectJsonFormat(
  { productId: numberJsonFormat, vendorId: numberJsonFormat },
  {},
);

const scaleStore = jsonStore(localStore("redo.scale"), deviceIdFormat);

const ScaleButton = memo(function ScaleButton({
  onWeightChange,
  disabled,
  disableAutoReadFromScale,
}: {
  onWeightChange: (weight: Weight, source?: WeightChangeSource) => void;
  disabled: boolean;
  disableAutoReadFromScale?: boolean;
}) {
  const [debugData, setDebugData] = useState<ArrayBuffer | undefined>();

  const devices = useContext(HidContext);
  const deviceRequest = useContext(HidRequestContext);

  const deviceId = scaleStore.get();
  const device =
    deviceId &&
    devices.find(
      (device) =>
        device.productId === deviceId.productId &&
        device.vendorId === deviceId.vendorId,
    );

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownRef, setDropdownRef] = useState<HTMLElement | null>(null);

  const [weightLoad, loadWeight] = useLoader<void>();

  const addDevice = async () => {
    const [device] = await deviceRequest(SCALE_FILTERS);
    if (!device) {
      return;
    }
    scaleStore.set(device);
    return device;
  };

  const fetchWeight = (event?: MouseEvent, device?: HIDDevice) =>
    loadWeight(async () => {
      if (!device) {
        device = await addDevice();
        if (!device) {
          return;
        }
      }
      await alertOnFailure("Failed to read weight from scale")(async () => {
        if ((event?.ctrlKey || event?.metaKey) && event?.shiftKey) {
          const report = await hidInputReport(device!);
          const buffer = new ArrayBuffer(report.data.byteLength);
          new Uint8Array(buffer).set(new Uint8Array(report.data.buffer));
          setDebugData(buffer);
          return;
        }
        const weight = await scaleReadWeight(device!);
        onWeightChange(
          {
            unit:
              weight.unit === ScaleWeightUnit.OUNCE
                ? WeightUnit.OUNCE
                : WeightUnit.GRAM,
            value: Math.max(0, weight.value),
          },
          "scale",
        );
      });
    });

  const readWeightContinuously = useHandler((device: HIDDevice) => {
    let stableCount = 0;
    let lastWeight: Weight | null = null;
    let lastStableWeight: Weight | null = null;

    const intervalId = setInterval(async () => {
      try {
        const weight = await scaleReadWeight(device);
        if (
          lastWeight &&
          Math.abs(weight.value - lastWeight.value) < 0.1 &&
          weight.value > 0.01
        ) {
          stableCount++;
        } else {
          stableCount = 0;
        }
        lastWeight = {
          value: weight.value,
          unit:
            weight.unit === ScaleWeightUnit.OUNCE
              ? WeightUnit.OUNCE
              : WeightUnit.GRAM,
        };

        if (
          stableCount >= 5 &&
          (!lastStableWeight ||
            Math.abs(weight.value - lastStableWeight.value) >= 0.1)
        ) {
          onWeightChange(
            {
              unit:
                weight.unit === ScaleWeightUnit.OUNCE
                  ? WeightUnit.OUNCE
                  : WeightUnit.GRAM,
              value: Math.max(0, weight.value),
            },
            "scale",
          );
          lastStableWeight = { ...lastWeight };
        }
      } catch (error) {
        // ignore
      }
    }, 250);

    return intervalId;
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (device && !disableAutoReadFromScale) {
      intervalId = readWeightContinuously(device);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [device, readWeightContinuously, disableAutoReadFromScale]);

  const interactWithScale = useSafeInteractWithScale();
  interactWithScale.readFromScale = () => fetchWeight(undefined, device);

  return (
    <>
      <RedoButtonDropdown
        disabled={disabled || !navigator.hid} // TODO: tooltip to indicate why disabled
        dropdownOpen={dropdownOpen}
        hierarchy="secondary"
        onClick={(event) => fetchWeight(event, device)}
        pending={weightLoad.pending}
        ref={setDropdownRef}
        setDropdownOpen={setDropdownOpen}
        size="sm"
        text="Use scale"
      >
        <RedoCommandMenu
          anchor={dropdownRef}
          items={[
            ...devices.map((d) => ({
              Icon: d === device ? CheckIcon : undefined,
              onClick(event: MouseEvent) {
                scaleStore.set(d);
                fetchWeight(event, d);
              },
              text: d.productName,
            })),
            { Icon: PlusIcon, text: "Add scale...", onClick: addDevice },
          ]}
          open={dropdownOpen}
          setOpen={setDropdownOpen}
        />
      </RedoButtonDropdown>
      {debugData && (
        <RedoModal
          onModalCloseRequested={() => setDebugData(undefined)}
          title="Scale Input"
        >
          Data: <pre style={{ display: "inline" }}>{dataString(debugData)}</pre>
        </RedoModal>
      )}
    </>
  );
});

function dataString(data: ArrayBuffer): string {
  return [...new Uint8Array(data)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join(" ");
}
